import * as THREE from 'three';
import { clamp, lerp } from '../core/math';
import { camera } from '../render/scene';
import { STATE, angDiff } from './fx';
import { CAM } from './camera';
import { P } from '../world/player';
/* ============================================================
   出擊過場 —— 從待機展示視角帶到遊玩視角
   ============================================================

   使用者 2026-08-06：「目前主畫面是機甲待機畫面，我想進入戰局是從這個視角
   拉遠帶到實際玩家視角，有個過渡動畫這樣。」選的是**兩段式出擊**（AC6 感）。

   時間軸（總長 1.94 秒）：

     0.00–0.80  段一：從展示鏡頭**繞**到機體後下方，低角度追尾
     0.80–1.02  停一拍：讓那個機位被看見（少了這一拍就只是一次滑動）
     1.02–1.94  段二：抬升拉遠到遊玩俯視角，落定時一記輕震

   ---- 為什麼不自己算戰場機位

   終點不是寫死的數字，而是**每幀去讀 `updateCamera` 算出來的結果**
   （`BAT`）。戰場鏡頭吃預設表、使用者滾輪縮放、密度拉遠、Boss 拉遠……
   複製一份就等於多一個會漂移的真相。代價是每幀要幫它保管狀態：
   `preCamIntro()` 把上一幀 `updateCamera` 的輸出還回 `camera.position`，
   它的 `damp` 才不會從我們的混合值續算（那會讓終點自己跟著跑）。

   ---- 為什麼位置走「方位／水平距離／高度」而不是直線內插

   直線從側面插到後方會**從機體身上穿過去**。拆成極座標之後，方位的差值
   自然畫出一道繞著機體的弧，而距離與高度各自獨立收斂 —— 這也是機庫鏡頭
   （`ui/hangar.ts`）用的同一套表述。朝向另外用四元數 slerp，不跟位置綁：
   位置要繞弧、視線要平順，兩者的最短路徑不是同一條。

   ---- 中繼機位在機體的**正後方**

   使用者 2026-08-06：「出擊是面向前面，不是面對我方。」出擊這一拍要看到的是
   機甲的背影朝著戰場走出去 —— 鏡頭擺在它正面就變成「它在看我」，那是登場
   展示不是出擊。所以中繼的方位綁 `P.faceAim + π`，不綁戰場鏡頭的方位。

   代價：機體朝向與戰場鏡頭方位夾角越大，段二要繞的角度越多。開局朝向是
   背對戰場鏡頭（朝畫面深處）時兩者剛好重合，段二零旋轉；真的岔開時就讓它
   繞 —— 繞的是**戰場鏡頭那一側**的短弧（`angDiff`），不會轉過頭。

   決定性：這個檔跑在畫面幀上，只寫 `camera` 與 `STATE.shake`（純視覺），
   不碰模擬狀態。過場期間 `game.ts` 不跑 `simStep` —— 玩家不會在還沒看清
   自己的時候被打，計時也還沒開始。 */

var SEG1 = 0.80;          // 繞到中繼機位
var HOLD = 0.22;          // 停一拍
var SEG2 = 0.92;          // 抬升拉遠
var DUR = SEG1 + HOLD + SEG2;

/** 中繼（低角度追尾）機位。距離近、高度低、FOV 寬 —— 這一拍要的是體積感。 */
var MID = { dist: 9.6, y: 3.4, look: 6.0, lookY: 2.5, fov: 53 };

var IN = {
  active: false,
  ready: false,           // 中繼機位算好了沒（要等玩家就位，見 init）
  t: 0,
  fromP: new THREE.Vector3(), fromQ: new THREE.Quaternion(), fromFov: 50,
  midQ: new THREE.Quaternion(),
  fr: { yaw: 0, h: 0, y: 0 },    // 起點相對玩家的方位／水平距離／高度
  mr: { yaw: 0, h: 0, y: 0 },    // 中繼同上
  cx: 0, cz: 0                   // 繞行中心（玩家）
};

/** 上一幀 `updateCamera` 的輸出。段二的終點，也是還給它續算 damp 的狀態。 */
var BAT = { pos: new THREE.Vector3(), quat: new THREE.Quaternion(), fov: 46, has: false };

var m4 = new THREE.Matrix4();
var UP = new THREE.Vector3(0, 1, 0);
var vA = new THREE.Vector3(), vB = new THREE.Vector3();
var qT = new THREE.Quaternion();

function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

function relOf(p, cx, cz) {
  var dx = p.x - cx, dz = p.z - cz;
  return { yaw: Math.atan2(dx, dz), h: Math.hypot(dx, dz), y: p.y };
}

export function camIntroActive() { return IN.active; }

/** 按下出擊時呼叫，**要排在 `startRun()` 之前** —— `startRun` 會走到
    `resetCamera()`，那會把 FOV 改成戰場預設值，晚一步抓就抓不到展示視角的
    起點了。只有 UI 的兩個出擊入口會呼叫它；bot 與測試走 `startRun` 直入，
    完全不受過場影響（也就不會每場多等兩秒）。 */
export function startCamIntro() {
  IN.active = true; IN.ready = false; IN.t = 0; BAT.has = false;
  IN.fromP.copy(camera.position);
  IN.fromQ.copy(camera.quaternion);
  IN.fromFov = camera.fov;
  /* 讓第一次 `updateCamera` 直接落在戰場機位，不要從展示位置 damp 過去：
     終點在移動的話，段二插的是一個活靶。 */
  CAM.snap = 1;
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', skipCamIntro);
    window.addEventListener('mousedown', skipCamIntro);
    window.addEventListener('touchstart', skipCamIntro);
  }
}

/** 任何輸入都跳過剩下的過場。**不硬切**：推到最後 0.3 秒讓它自己收尾 ——
    硬切會讓畫面瞬移，讀起來像掉幀而不是「我跳過了」。 */
export function skipCamIntro() {
  if (!IN.active) return;
  IN.t = Math.max(IN.t, DUR - 0.30);
}

function init() {
  IN.cx = P.viewX; IN.cz = P.viewZ;
  IN.fr = relOf(IN.fromP, IN.cx, IN.cz);
  // 機體的正後方：`faceAim` 指的是機體的正面，退到它反向就是背影那一側
  var back = P.faceAim + Math.PI;
  IN.mr = { yaw: back, h: MID.dist, y: MID.y };
  // 視線越過機體看向它**面對的方向**，機體落在畫面中央偏下、前方戰場展開
  vA.set(IN.cx + Math.sin(back) * IN.mr.h, IN.mr.y, IN.cz + Math.cos(back) * IN.mr.h);
  vB.set(IN.cx + Math.sin(P.faceAim) * MID.look, MID.lookY, IN.cz + Math.cos(P.faceAim) * MID.look);
  m4.lookAt(vA, vB, UP);
  IN.midQ.setFromRotationMatrix(m4);
  IN.ready = true;
}

/** 排在 `updateCamera` **之前**：把它上一幀的輸出還回去，讓它的 damp 從
    自己的狀態續算，而不是從我們寫進 camera 的混合值。 */
export function preCamIntro() {
  if (!IN.active || !BAT.has) return;
  camera.position.copy(BAT.pos);
}

/** 排在 `updateCamera` **之後**：先收下它算的戰場機位，再蓋上過場的混合值。 */
export function applyCamIntro(dt) {
  if (!IN.active) return;
  BAT.pos.copy(camera.position); BAT.quat.copy(camera.quaternion); BAT.fov = camera.fov; BAT.has = true;
  if (!IN.ready) init();

  IN.t += dt;
  var yaw, h, y, fov;
  if (IN.t < SEG1 + HOLD) {
    var e = ease(clamp(IN.t / SEG1, 0, 1));
    yaw = IN.fr.yaw + angDiff(IN.mr.yaw, IN.fr.yaw) * e;
    h = lerp(IN.fr.h, IN.mr.h, e);
    y = lerp(IN.fr.y, IN.mr.y, e);
    fov = lerp(IN.fromFov, MID.fov, e);
    qT.copy(IN.fromQ).slerp(IN.midQ, e);
  } else {
    var br = relOf(BAT.pos, IN.cx, IN.cz);
    var e2 = ease(clamp((IN.t - SEG1 - HOLD) / SEG2, 0, 1));
    yaw = IN.mr.yaw + angDiff(br.yaw, IN.mr.yaw) * e2;
    h = lerp(IN.mr.h, br.h, e2);
    y = lerp(IN.mr.y, br.y, e2);
    fov = lerp(MID.fov, BAT.fov, e2);
    qT.copy(IN.midQ).slerp(BAT.quat, e2);
  }
  camera.position.set(IN.cx + Math.sin(yaw) * h, y, IN.cz + Math.cos(yaw) * h);
  camera.quaternion.copy(qT);
  if (Math.abs(camera.fov - fov) > 0.01) { camera.fov = fov; camera.updateProjectionMatrix(); }

  if (IN.t >= DUR) end();
}

function end() {
  IN.active = false; IN.ready = false;
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', skipCamIntro);
    window.removeEventListener('mousedown', skipCamIntro);
    window.removeEventListener('touchstart', skipCamIntro);
  }
  // 精確交回戰場鏡頭（上面那組是用極座標重建的，留浮點誤差沒有意義）
  camera.position.copy(BAT.pos); camera.quaternion.copy(BAT.quat);
  // 落定的一記輕震：鏡頭「停下來」需要一個實體的理由，不然它只是停了
  STATE.shake = Math.max(STATE.shake, 0.22);
}
